import { Injectable, NgZone, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { WithoutInterceptor } from './without.interceptor';

/** Per-status counts of the booking durable request queue (booking_outbox). */
export interface OutboxSummary {
    PENDING: number;
    PROCESSING: number;
    COMPLETED: number;
    FAILED: number;
    DISCARDED: number;
}

export interface OutboxRow {
    id: number;
    bookingRefId: string;
    counter: number;
    status: string;
    errorMessage: string;
    attempts: number;
    createdAt: string;
    invoiceAt: string;
    familyName: string;
}

/**
 * Polls `v2/pooja/booking/outbox/summary` for the toolbar queue indicator.
 * Uses WithoutInterceptor so the background poll never triggers the global
 * loader spinner; the timer runs outside the Angular zone.
 */
@Injectable({ providedIn: 'root' })
export class BookingQueueStatusService {

    readonly summary = signal<OutboxSummary | null>(null);
    readonly unreachable = signal(false);

    private timerId: any = null;
    private consumers = 0;
    // R52: pause polling while the window is hidden (minimized / another app in
    // front on some platforms). Counters sit idle for hours — those polls are
    // pure waste. Bound once so add/removeEventListener get the same reference.
    private visibilityListener: (() => void) | null = null;

    constructor(private http: WithoutInterceptor, private zone: NgZone) { }

    /** Reference-counted so multiple widgets share one poll loop. */
    startPolling(intervalMs = 15000) {
        this.consumers++;
        if (this.timerId !== null) {
            return;
        }
        this.refresh();
        this.zone.runOutsideAngular(() => {
            this.timerId = setInterval(() => this.refresh(), intervalMs);
            // R52: refresh immediately when the window becomes visible again so
            // the indicator is fresh the moment the operator returns.
            this.visibilityListener = () => {
                if (!document.hidden) {
                    this.refresh();
                }
            };
            document.addEventListener('visibilitychange', this.visibilityListener);
        });
    }

    stopPolling() {
        this.consumers = Math.max(0, this.consumers - 1);
        if (this.consumers === 0 && this.timerId !== null) {
            clearInterval(this.timerId);
            this.timerId = null;
            if (this.visibilityListener) {
                document.removeEventListener('visibilitychange', this.visibilityListener);
                this.visibilityListener = null;
            }
        }
    }

    refresh() {
        // R52: skip the poll entirely while the document is hidden — the timer
        // keeps ticking but no request leaves; the visibilitychange listener
        // fires a catch-up refresh when the window is shown again.
        if (document.hidden) {
            return;
        }
        if (!this.hasSession()) {
            return;
        }
        this.http.get<any>(environment.apiUrl + 'v2/pooja/booking/outbox/summary').subscribe({
            next: (resp) => this.zone.run(() => {
                this.summary.set(resp?.result || null);
                this.unreachable.set(!resp?.result);
            }),
            error: () => this.zone.run(() => this.unreachable.set(true))
        });
    }

    failedRows(limit = 20): Observable<any> {
        return this.http.get<any>(environment.apiUrl + `v2/pooja/booking/outbox?status=FAILED&limit=${limit}`);
    }

    requeue(id: number): Observable<any> {
        return this.http.post<any>(environment.apiUrl + `v2/pooja/booking/outbox/${id}/requeue`, {});
    }

    /** Releases the row's limited-pooja reservations and marks it DISCARDED. */
    discard(id: number): Observable<any> {
        return this.http.post<any>(environment.apiUrl + `v2/pooja/booking/outbox/${id}/discard`, {});
    }

    private hasSession(): boolean {
        try {
            const user = sessionStorage.getItem('CURRENT_USER');
            return !!user && !!JSON.parse(user)?.accessToken;
        } catch {
            return false;
        }
    }
}
